import { useEffect } from 'react'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import { styled, keyframes } from '@mui/material/styles'
import { useAtom, useAtomValue } from 'jotai'
import { loadingPercentAtom, isWebReadyAtom } from '@/core/store/startup.store'

// ─── Animation ────────────────────────────────────────────────────────────────

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`

// ─── Styled components ────────────────────────────────────────────────────────

const Overlay = styled(Stack)<{ ready: number }>`
    position: fixed;
    inset: 0;
    background-color: #fff;
    z-index: ${({ ready }) => (ready ? -1 : 9999)};
    display: ${({ ready }) => (ready ? 'none' : 'flex')};
    animation: ${({ ready }) => (ready ? `${fadeOut} 0.6s ease forwards` : 'none')};
`

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: '#d1e3f5',
    '& .MuiLinearProgress-bar': {
        borderRadius: 4,
        backgroundColor: theme.palette.primary.main
    }
}))

// ─── PageLoadingOverlay ───────────────────────────────────────────────────────

export interface PageLoadingOverlayProps {
    /** 0-100 for determinate, omit for indeterminate animation. */
    percent?: number
}

/**
 * Reusable full-screen white overlay with a centered progress bar.
 * Used as the Suspense fallback for route-level modules so navigating to
 * a lazy-loaded page shows the same loader as the initial app boot.
 */
export const PageLoadingOverlay = ({ percent }: PageLoadingOverlayProps) => (
    <Overlay ready={0} alignItems="center" justifyContent="center" data-testid="loading-bar-overlay">
        <Box width={200} data-testid="loading-bar-box">
            {percent === undefined ? (
                <BorderLinearProgress data-testid="loading-bar-border-linear-progress-1" />
            ) : (
                <BorderLinearProgress variant="determinate" value={percent} data-testid="loading-bar-border-linear-progress-2" />
            )}
        </Box>
    </Overlay>
)

// ─── LoadingBar (app-startup) ─────────────────────────────────────────────────

/**
 * Full-screen loading overlay. Renders at the root level (above BrowserRouter).
 * Hides itself once all 3 startup keys are loaded (with a 1 s UX delay).
 *
 * Place in App.tsx:
 *   return (
 *     <>
 *       <LoadingBar />
 *       <BrowserRouter>...</BrowserRouter>
 *     </>
 *   )
 */
export const LoadingBar = () => {
    const percent = useAtomValue(loadingPercentAtom)
    const [isReady, setIsReady] = useAtom(isWebReadyAtom)

    // Mirror the original 1 s intentional UX delay before revealing the app.
    // Self-disposes via cleanup — safe in Strict Mode.
    useEffect(() => {
        if (percent >= 100 && !isReady) {
            const timer = setTimeout(() => setIsReady(true), 1000)
            return () => clearTimeout(timer)
        }
    }, [percent, isReady, setIsReady])

    if (isReady) return null

    return <PageLoadingOverlay percent={percent} data-testid="loading-bar-page-loading-overlay" />
}

export default LoadingBar
